import type { ReactNode } from "react";
import { StyleSheet, Text, View } from "react-native";
import type { Palette } from "@heraion/shared";

// The console top bar (web-rnw shell chrome). One bar for BOTH layouts — the
// desktop console (full-width, above the [rail | content] row) and the narrow
// phone-width browser (above the tab bar). It carries the brand and the
// account menu pinned right; the role / alerts / settings row sits under it
// (#208). RW-52 moved Create out of here, into the nav
// (rail/tab bar), where it sits on mobile — the TopBar no longer owns any
// action, only identity + brand.
//
// App-shell chrome lives in apps/web-rnw/src/shell/, not @heraion/ui: mobile
// has no rail/top-bar dichotomy (native stack header + bottom tabs), so this is
// not RN-portable and does not belong in the cross-platform primitive layer.
// It replaces the hand-rolled narrow headerBar, so the brand + trailing-slot
// shape is encoded once instead of twice.
export function TopBar({
  p,
  brand,
  right,
  maxWidth,
}: {
  p: Palette;
  brand: string;
  /** Pinned to the right edge: the account menu. */
  right: ReactNode;
  /** Narrow centres its inner row to the shared content width; wide spans the
   *  whole bar (brand aligns with the rail below). */
  maxWidth?: number;
}) {
  const styles = makeStyles(p);
  return (
    <View style={styles.bar}>
      <View style={[styles.inner, maxWidth != null && { maxWidth, marginHorizontal: "auto" }]}>
        {/* On a phone-width browser in the long locales the brand can near
            the row's edge, so it wraps instead of pushing the account
            trigger — the only path to Settings/Sign-out — off-screen. */}
        <View style={styles.lead}>
          <Text style={styles.brand} numberOfLines={1}>
            {brand}
          </Text>
        </View>
        <View style={styles.spacer} />
        <View style={styles.right}>{right}</View>
      </View>
    </View>
  );
}

function makeStyles(p: Palette) {
  return StyleSheet.create({
    bar: {
      backgroundColor: p.surface,
      borderBottomWidth: 1,
      borderBottomColor: p.border,
      // The account menu opens DOWNWARD from here over the content below, which
      // is a later sibling in the shell column and would otherwise paint on top.
      // Lift the whole bar above it so the (anchor-zIndex-lifted) menu shows.
      zIndex: 20,
    },
    inner: {
      width: "100%",
      flexDirection: "row",
      alignItems: "center",
      flexWrap: "wrap",
      rowGap: 8,
      columnGap: 12,
      minHeight: 56,
      paddingVertical: 8,
      paddingHorizontal: 18,
    },
    // Brand + Create as one shrinkable unit so a long CTA can't shove the
    // account trigger off the row.
    lead: { flexShrink: 1, flexDirection: "row", alignItems: "center", gap: 10 },
    brand: { color: p.brand500, fontSize: 18, fontWeight: "700", flexShrink: 1 },
    spacer: { flex: 1 },
    right: { flexDirection: "row", alignItems: "center", flexShrink: 0, columnGap: 12 },
  });
}
